/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @file Main entry point for default neuroglancer viewer.
 */
import { setupDefaultViewer } from 'neuroglancer/ui/default_viewer_setup';
import { displayLoadingNone, noPermision, makeElementUtil, responseJson } from './neuroglancer/util/http_request';
import { cancellableFetchSpecialOk } from './neuroglancer/util/special_protocol_request';
import { Viewer, isSelectView } from './neuroglancer/viewer';
import { message } from './neuroglancer/widget/message';
import i18n from './neuroglancer/i18n/i18n';
import { comm_arr_filter } from './neuroglancer/util/comm_find';
// import {_checkColor} from './neuroglancer/ui/layer_gene_table_tab'
import { UserPemission } from './neuroglancer/pemission';
import { getNewRpiImageType, image_layer_info_interface } from './neuroglancer/util/comm_fun_hq';
import { makeElement } from "neuroglancer/ui/layer_gene_table_tab";
import { OpenFilesDialog } from "neuroglancer/ui/open_files_dialog";
import { interceptorHeaderInfo, headerMap } from "neuroglancer/util/http_interceptors_base";
import { tissueGefOutline } from './neuroglancer/ui/tissue';
import { dbStorageMoreWindow } from 'neuroglancer/util/indexDb';

export let viewer: Viewer;
// export let self_newlabel:{[K:string]: string} = {};
export let resetJson: any = {};
export let umap_tree_bin_arr: any = [];              //  存储umap 图像的binsize

/**
 * genename: 用于标识当前图层的名称,annotationCellbin: 标记为anno + cellbin中的cellbin/bin1
 * genetype：用于标识当前图层的类型：
 *  proteinimage：为蛋白image类型,proteinimage_image: 为带颜色的image图像
 *  annotationdnb: dnb类型
 *  IFimage: IF配准图
 *  splicePic: 小图拼接
 * genecolor：用于标识当前图层的颜色：常见于蛋白中
 * iscellbindnb：用于标识当前是否为cellbin与dnb的互斥
 * geneTableUrl: 用于标识当前类型的genetable请求地址
 * allToolsButton: 所有工具按钮的开关
 * hasData: 用来标识图层是否有数据，false-屏蔽到对应数据发送请求，true-正常请求数据
 * heatmap: boolean : 用于标识是否为annotation的基因热图，主要应用于bin200区分annotation类型是否热图
 * menu_path: 用来存储文件路径的，主要使用在workflow投递中
 * processid：文件的流程号，主要使用在workflow投递中
 * filename：文件名称，主要使用在workflow投递中
 * sample_id：sn，芯片号，主要使用在workflow投递中
 */

(function () {
  const originalSetItem = window.localStorage.setItem;
  const originalGetItem = window.localStorage.getItem;

  window.localStorage.setItem = function (key, value) {
    const prefixedKey = 'stmapV3:' + key;
    originalSetItem.call(this, prefixedKey, value);
  };

  window.localStorage.getItem = function (key) {
    const prefixedKey = 'stmapV3:' + key;
    return originalGetItem.call(this, prefixedKey);
  };
})();


const curr_date = new Date().getTime() / 1000.0;
let cookieObj: any;
let isMoveFlag: boolean = false;
//  export const _localIP_host: any = 'http://172.19.197.5:8899/ngdataserverv3/';
export const _localIP_host: any = localStorage.getItem('_TEST_URL') || process.env.domainConfig![process.env.NODE_ENV!].stereomapDomain;
export let local_host_SN_flag: string;
export let local_dataId: string;
export let userPemission: UserPemission = { // 用户权限
  tool: true,                   //  工具按钮权限
  toolbar: true,                //  工具栏按钮权限
  statusbar: true,              //  状态栏按钮权限
  lasso: true,                  //  套索按钮权限
  register: true,               //  配准按钮权限
  saveState: true,              //  保存状态按钮权限
  exportImage: true,            //  导出高清图按钮权限
  midFilter: true,              //  MIDFilter输出按钮权限
}
export let allToolsButton: {
  [K: string]: boolean | string | Array<[string]> | { [K: string]: string } | any
} = {};
// flag为'customize'表示为第三方工具调用可视化，传参数据通过cookie中的stereo_map
interface StereoMap {
  host_path: String,
  authority?: Array<string>,
  gef?: String,
  cgef?: String,
  cell_border_path?: String,
  rpi?: String,
  transformed?: String,
  h5ad?: String,
  token?: String
}
export let _param: { [K: string]: string } = {};
export let _check_image_Color = ['0000ff', '00ffff', 'ff00ff', '00ff00', 'ff0000', 'ff9600'];  //
let statisticsFile: string[] = [];
let hasImage: boolean = false;
let is_label_gef: boolean = false;    // 是否含有label。gef文件
export let h5ad_file_binsize = 'bin200';   //  umap默认binsize
let transcript_gef = '';
let protein_gef = '';
let initShowLegend: boolean = false;
export let is_h5ad_file: boolean | undefined = undefined;   //  是否含有h5ad文件，用于保存打开状态时读取
export let gef_binlist: any = {};                                   //  由于可以打开多个gef，现需要存储不同gef下对应的binlist

let layerinfo_permission: any = { layerinfo: {}, permission: {} };
//  用户权限

const page_init_map = new Map();
const page_init_default_fun = async () => {
  await getLayers();
  let res = layerinfo_permission.layerinfo;
  if (res.code === 0) {
    runViewer(_param.DataID ? _param.DataID : '', _param.flag ? _param.flag : 'v1', res.data);
  } else {
    new message(`${i18n.asyncfail}${res.msg}`, 'error');
  }
}
const page_init_demo_fun = async () => {
  if (_param.zone) {
    interceptorHeaderInfo.value[headerMap['zone']] = _param.zone;
  }
  let demo_res = _param.userOpId ? await getDemoJson(_param.userOpId) : undefined;
  await getLayers();
  let res = layerinfo_permission.layerinfo;
  if (res.code === 0) {
    runViewer(_param.DataID ? _param.DataID : '', _param.flag ? _param.flag : 'v1', res.data, demo_res);
  } else {
    new message(`${i18n.asyncfail}${res.msg}`, 'error');
  }
}
const page_init_customize_fun = async () => {
  userPemission = { // 默认没有权限
    tool: false,
    toolbar: true,
    statusbar: true,
    lasso: false,
    register: false,
    saveState: false,
    exportImage: false,
    midFilter: false,
  }
  // 设置可视化权限
  let stereoMapString = localStorage.getItem('stereo_map')
  if (stereoMapString) {
    const stereo_map: StereoMap = JSON.parse(stereoMapString)
    const { authority = [] } = stereo_map
    authority.forEach((item: string) => {
      userPemission[item] = true
    })
  }
  await getLayers();
  let res = layerinfo_permission.layerinfo;
  if (res.code === 0) {
    runViewer(_param.DataID ? _param.DataID : '', _param.flag ? _param.flag : 'v1', res.data);
  } else {
    new message(`${i18n.asyncfail}${res.msg}`, 'error');
  }
}
const page_init_token_fun = async () => {
  const exp = _param.token ? cookieObj.exp : Number(localStorage.getItem('exp'))
  if (isOperate(exp - curr_date)) {
    for (let i in cookieObj) {
      if (headerMap.hasOwnProperty(i)) {
        localStorage.setItem(i, cookieObj[i]);
        interceptorHeaderInfo.value[headerMap[i]] = cookieObj[i];
      }
    }
    if (_param.token) {
      interceptorHeaderInfo.value[headerMap['skfx_token']] = _param.token;
      localStorage.setItem('ksh_token', _param.token);
    } else {
      interceptorHeaderInfo.value[headerMap['skfx_token']] = localStorage.getItem('ksh_token');
    }
    if (_param.zone) {
      interceptorHeaderInfo.value[headerMap['zone']] = _param.zone;
    }
    if (_param.lang) sessionStorage.setItem('lang', _param.lang)
    local_dataId = _param.DataID;
    numcount();
    let response: any = await getFilePathPermission();
    if (response.code !== 0) return;
    await getLayers();
    // let res = await getLayers();
    let res = layerinfo_permission.layerinfo;
    if (res.code === 0) {
      changeURLStatic('token', '');
      if (_param.lang) changeURLStatic('lang', '');
      if (_param.sessionId) changeURLStatic('sessionId', '');
      runViewer(_param.DataID ? _param.DataID : '', _param.flag ? _param.flag : 'v1', res.data);
    } else {
      new message(`${i18n.asyncfail}${res.msg}`, 'error');
    }
  } else {
    console.log('首次登录过期')
    noPermision();
  }
}
const page_init_tool_fun = async () => {
  const exp = _param.token ? cookieObj.exp : Number(localStorage.getItem('exp'))
  if (isOperate(exp - curr_date)) {
    for (let i in cookieObj) {
      if (headerMap.hasOwnProperty(i)) {
        localStorage.setItem(i, cookieObj[i]);
        interceptorHeaderInfo.value[headerMap[i]] = cookieObj[i];
      }
    }
    if (_param.token) {
      interceptorHeaderInfo.value[headerMap['skfx_token']] = _param.token;
      localStorage.setItem('ksh_token', _param.token);
      changeURLStatic('token', '');
    } else {
      interceptorHeaderInfo.value[headerMap['skfx_token']] = localStorage.getItem('ksh_token');
    }
    if (_param.zone) {
      interceptorHeaderInfo.value[headerMap['zone']] = _param.zone;
    }
    if (_param.lang) changeURLStatic('lang', '');
    numcount();
    const defaultInfo = {
      "bin_list": [
        "bin100",
      ],
      "default_bin_size": "bin100",
      "isStandardAnalysis": false,
      "isTransform": null,
      "layers": [
      ],
      "omics": "Transcriptomics"
    };
    const ngContainer = document.getElementById('neuroglancer-container') as HTMLElement
    ngContainer.innerHTML = '';
    const noDataWrap = makeElement('div', ['no-data-wrap'], { 'style': 'position: relative;width: 100%;height: 100%;z-index: 3;display: flex;align-items: center;justify-content: center;flex-direction: column;' },
      `<img src="static/no_data.png" /><p style="font-size: 2rem;line-height: 31px;font-family: MicrosoftYaHei; margin:34px 0; color: #999999;">${i18n.nodata}</p>`)
    ngContainer.appendChild(noDataWrap);
    const openFileButton = makeElement('button', ['open_file'], { title: `${i18n.open}` }, `${i18n.open}`);
    noDataWrap.appendChild(openFileButton);
    openFileButton.addEventListener('click', () => {
      viewer.openFilesDialog = new OpenFilesDialog();
    })

    displayLoadingNone();
    runViewer('', 'toolDefault', defaultInfo, false);
  } else {
    console.log('首次登录过期')
    noPermision();
    displayLoadingNone();
  }
}
page_init_map.set('default', page_init_default_fun)
page_init_map.set('demo', page_init_demo_fun)
page_init_map.set('customize', page_init_customize_fun)
page_init_map.set('token', page_init_token_fun)
page_init_map.set('tool', page_init_tool_fun)
window.addEventListener('DOMContentLoaded', async () => {
  if (dbStorageMoreWindow) {
    let tempData: any = await dbStorageMoreWindow.readByIndex('layerName', 'name', 'value');
    if (!tempData) {
      await dbStorageMoreWindow.add('layerName', { name: 'value', data: {} })
    }
  }
  _check_image_Color = ['0000ff', '00ffff', 'ff00ff', '00ff00', 'ff0000', 'ff9600'];  //  蛋白基础色
  if (location.href.includes('?')) {
    _param = formatterURLHREF(location.href);
    allToolsButton.projectId = _param.projectId ?? 'projectid';
    local_host_SN_flag = `${_localIP_host}${_param.DataID}/${_param.flag}/${allToolsButton.projectId}`;
    localStorage.setItem('genesn', _param.sn);
    if (_param.lang) sessionStorage.setItem('lang', _param.lang)
    interceptorHeaderInfo.value[headerMap['lang']] = _param.lang || sessionStorage.getItem('lang');
    // default：不用鉴权，用于开发测试，demo：用于demo数据，不用鉴权，需控制工具按钮和状态按钮
    switch (_param.flag) {
      case 'default':
        await page_init_map.get('default')();
        break;
      case 'demo':
        await page_init_map.get('demo')();
        break;
      case 'customize':
        page_init_map.get('customize')();
        break;
      default:
        if (_param.token || localStorage.getItem('ksh_token')) {
          clearAllCookie();
          if (_param.token) {
            cookieObj = decodeToken(_param.token);
          } else {
            const token = localStorage.getItem('ksh_token');
            if (token) cookieObj = decodeToken(token);
          }
          if (_param.flag === "tool" && !_param.DataID) {
            await page_init_map.get('tool')();
          } else {
            await page_init_map.get('token')();
          }
        } else {
          console.log('stroage no cookie')
          noPermision();
        }
    }
    displayLoadingNone();
  } else {
    viewer = setupDefaultViewer();
  }
  let lang = window.sessionStorage.getItem('lang') || 'en';
  if (navigator.platform.toLocaleLowerCase().includes('win')) {
    // windows
    document.body.style.fontFamily = lang === 'cn' ? 'MicrosoftYaHei' : 'Airal'
  } else if (navigator.platform.toLocaleLowerCase().includes('mac')) {
    // mac
    document.body.style.fontFamily = lang === 'cn' ? 'San francisco' : 'PingFangSC-Regular,PingFang SC'
  } else {
    document.body.style.fontFamily = 'Airal, MicrosoftYaHei'
  }
  window.onmousemove = () => {
    if (!location.href.includes('#!') && Number(localStorage.getItem('exp')) - new Date().getTime() / 1000.0 < 1800) {
      isMoveFlag = true;
    }
  }
});

window.onbeforeunload = () => {
  console.log('关闭窗口', viewer.proteinsFilter.childWinArr)
  localStorage.removeItem('layerName')
  viewer.proteinsFilter.childWinArr?.forEach(item => {
    item.close()
  });
}

window.requestAnimationFrame(() => {
  console.log('刷新了', viewer?.proteinsFilter.childWinArr)
  localStorage.removeItem('layerName')
  viewer?.proteinsFilter.childWinArr?.forEach(item => {
    item.close()
  });
})

let liandong_value: any = undefined;
window.addEventListener('storage', async function (event) {
  console.log('New message:', event);
  let tempData: any = await dbStorageMoreWindow.readByIndex('layerName', 'name', 'value'), obj: any;
  tempData && (obj = tempData.data);
  console.log(obj);
  if ((event.currentTarget as Window)) {
    if (_param.visible) {
      viewer.proteinsFilter.xAndYInfo.value = obj.value;
      liandong_value = obj.value;
      if (obj.href) {
        viewer.proteinsFilter.xAndYInfo.value = [[], []]
        liandong_value = [[], []];
        viewer.proteinsFilter.binSize.value = obj.href;
      } else {
        console.log(obj.bin200_self_newlabel_color)
        if (obj.bin200_self_newlabel) {
          viewer.bin200_self_newlabel = obj.bin200_self_newlabel;
        }
        if (obj.bin200_self_newlabel_color) {
          viewer.bin200_self_newlabel_color = obj.bin200_self_newlabel_color;
        }
        viewer.proteinsFilter.xAndYInfo.value = liandong_value;
        viewer.state.restoreState(viewer.state.toJSON())
      }
    }
  }
});

export function hasCluster(name: string): boolean {
  return name.indexOf('cluster') > -1 || name.indexOf('UMAP') > -1
}

function decodeToken(token: string) {
  localStorage.setItem('token', token);
  var string = token.replace(/%/g, '%25').split('.');
  var userinfo = JSON.parse(decodeURIComponent(escape(window.atob(string[1].replace(/-/g, "+").replace(/_/g, "/")))));
  return userinfo;
}

function numcount() {
  const time = setInterval(async () => {
    if (_param.flag === 'v2' || _param.flag === 'tool') {
      const req_str = Number(localStorage.getItem('exp')) - new Date().getTime() / 1000.0 < 1800 && isMoveFlag ? 'refresh_expiration' : 'is_login'
      let reqs = await fetch(`${local_host_SN_flag}/${req_str}`, { headers: interceptorHeaderInfo.value });
      if (reqs.status === 200) {
        if (req_str === 'refresh_expiration') {
          isMoveFlag = false;
          const aba = JSON.parse(await reqs.text());
          if (aba.code === 0 && aba.data) {
            const cookieObj = decodeToken(aba.data.token);
            localStorage.setItem('exp', cookieObj.exp);
            localStorage.setItem('username', cookieObj.username);
            localStorage.setItem('skfx_token', aba.data.token);
            localStorage.setItem('ksh_token', aba.data.token);
            interceptorHeaderInfo.value[headerMap['exp']] = cookieObj.exp;
            interceptorHeaderInfo.value[headerMap['username']] = cookieObj.username;
            interceptorHeaderInfo.value[headerMap['skfx_token']] = aba.data.token;
            interceptorHeaderInfo.value[headerMap['ksh_token']] = aba.data.token;
          }
        } else if (!((await reqs.json()).data.is_loging)) {
          displayLoadingNone();
          noPermision();
          clearInterval(time)
        }
      } else {
        displayLoadingNone();
        noPermision();
        clearInterval(time)
      }
    } else {
      localStorage.setItem('exp', Number(localStorage.getItem('exp')) + 60 * 60 + '');
    }
  }, 60000)
}

// 过期
function isOperate(time: number) {
  if (time < 0) {
    console.log('inter isOperate')
    noPermision();
    clearAllCookie();
    return false
  }
  return true
}

export function formatterURLHREF(url: any) {
  const urlParams = url.split('?')[1]?.split('&');
  let obj: any = {};
  for (let i = 0; i < urlParams.length; i++) {
    const curParam = urlParams[i].split('=');
    const value = curParam.length > 2 ? curParam.slice(1, curParam.length).join('=') : curParam[1]
    obj[curParam[0]] = value;
  };
  return obj;
}

// 清除所有cookie
function clearAllCookie() {
  var keys = document.cookie.match(/[^ =;]+(?=\=)/g);
  if (keys) {
    document.cookie = '';
  }
}
const checkApiPerms = (permission: string, permissionList: Record<string, any>) => {
  // 如果权限字符串包含冒号，按层级查找
  if (permission.includes(':')) {
    const keys = permission.split(':')
    let current = permissionList

    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key]
      } else {
        return false
      }
    }
    return true
  }

  // 不包含冒号的情况，直接查找
  return permission in permissionList
}

function changeURLStatic(name: string, value: string) {
  var url = location.href;
  console.log(name)
  var reg = evil('/([\?|&]' + name + '=)[^&]*/gi');
  value = value.toString().replace(/(^\s*)|(\s*$)/g, "");  //移除首尾空格
  if (!value) {
    var url2 = url.replace(reg, '');  //正则替换
    if (url2.includes('/&')) url2 = url2.replace('/&', '/?');  //正则替换
  } else {
    if (url.match(reg)) {
      var url2 = url.replace(reg, '$1' + value);  //正则替换
      if (url2.includes('/&')) url2 = url2.replace('/&', '/?');  //正则替换
    } else {
      var url2 = url + (url.indexOf('?') > -1 ? '&' : '?') + name + '=' + value;  //没有参数添加参数
    }
  }
  history.replaceState(null, '', url2);  //替换地址栏
}

interface SingleLayerInfo {
  "num_channels"?: number,
  "name": string,
  "type": string,
  "suffix": string,
  "file": string,
  "children"?: Array<SingleLayerInfo>
  "color": string,
  "has_data": any,
  "gray_level_elbow"?: number,
  "max_gray_level"?: number,
  "min_gray_level"?: number
  "_file": string,
  "sample_id": string,
  "process_id"?: string,
  "task_id"?: string,
  "menu_path"?: string,
  file_name?: string,
  _file_name?: string
}
async function runViewer(sn: string = 'test', source: string, res: any, demo_res: any | undefined = undefined) {
  let layer = res.layers;
  if (source !== 'default') {
    if (source === 'demo' || source === 'toolDefault') {
      userPemission = {
        toolbar: true,
        statusbar: false,
        lasso: false,
        register: false,
        saveState: false,
        exportImage: false,
        midFilter: false,
      }
    } else {
      // const _userPemission = await getPermission() ;
      try {
        const _userPemission = JSON.parse(layerinfo_permission.permission).codeTree;
        console.log('_userPemission--', JSON.parse(layerinfo_permission.permission))
        userPemission = {
          tool: true, // 控制游标、过滤、批注、模板
          toolbar: checkApiPerms('project_management:interactive_tool:stereo_map:toolbar', _userPemission) ?? false,
          statusbar: checkApiPerms('project_management:interactive_tool:stereo_map:statusbar', _userPemission) ?? false,
          lasso: checkApiPerms('project_management:interactive_tool:stereo_map:toolbar:lasso', _userPemission) ?? false,
          register: checkApiPerms('project_management:interactive_tool:stereo_map:toolbar:register', _userPemission) ?? false,
          saveState: checkApiPerms('project_management:interactive_tool:stereo_map:statusbar:save_and_load', _userPemission) ?? false,
          exportImage: checkApiPerms('project_management:interactive_tool:stereo_map:statusbar:download', _userPemission) ?? false,
          midFilter: checkApiPerms('project_management:interactive_tool:stereo_map:toolbar:export_mid_filter_file', _userPemission) ?? false,
        }
      } catch (error) {
        console.error(error)
      }

    }
  }
  allToolsButton = {
    isIFMerge: res?.isIFMerge ?? false,
    isRegister: res?.isRegister ?? false,
    isIFRegister: false,      //   暂时屏蔽
    isStandardAnalysis: res?.isStandardAnalysis ?? false,
    isTransform: res?.isTransform ?? false,
    isCellbinDnb: res?.isCellbinDnb ?? false,
    omics: res.omics ?? 'Transcriptomics',
    bin_list: res.bin_list,
    currentBinsize: res.default_bin_size ?? 'bin50',
    sn: sn,
    source: source,
    genelistFile: _param.genelistFile ?? '',     //   选中的heatmap文件名
    file_h5ad: '',     //  h5ad文件名
    is_match_binsize: true,   //   是否有匹配的binsize
    genelistName: 'null',
    projectId: allToolsButton.projectId,
    iprFileSN: res?.iprFileSN,            //  交互式工具配准sn号优先填充为ipr文件的sn号
    genelistSn: '',
  }
  viewer = setupDefaultViewer();
  viewer.binSize.value = _param.binSize ? _param.binSize : res.default_bin_size;
  viewer.color_scale = {};
  viewer.allDataSource.proteinIF = res?.proteinIF ?? [];
  viewer.allDataSource.imageType = layer?.filter((item: any) => item.type === 'image')[0]?.suffix ?? [];
  let json = viewer.state.toJSON();
  json.layout = 'xy';
  json.layers = [];
  json.binSize = _param.binSize ? _param.binSize : res.default_bin_size ?? 'bin100';
  const param: getNgLayersParam = {
    layerTree: layer,
    binsize: String(json.binSize),
    json: json,
    options: { sn, source },
    layerState: {},
    isChangeBinsize: false
  }
  getNgLayers(param, '', local_host_SN_flag, allToolsButton, _check_image_Color);
  await getAllStatictis();
  if (!layer?.length && source !== 'toolDefault') {
    new message(i18n.nodata, 'info');
  }
  json.genePanel = {
    "visible": true
  }
  localStorage.setItem('genesn', sn);
  viewer.layerTree = layer
  viewer.state.reset();
  let oonly_single_heatmap_visible = false;    //  是否有gef图像展示
  if (!_param.visible) {
    for (let i = 0; i < json.layers.length; i++) {
      if (is_label_gef && json.layers[i].heatmap) {
        // 默认展示全基因heatmap(不带label)
        json.layers[i].visible = !json.layers[i].genename.includes('label')
      }
      if (is_label_gef && layer.length < 2 && json.layers[i].heatmap) {
        // 有label.gef 没有image图像就展示第一个热图
        json.layers[i].visible = true;
      }
      if (!is_label_gef && json.layers[i].heatmap) {
        // 没有label.gef,就展示第一个热图
        json.layers[i].visible = !oonly_single_heatmap_visible;
        oonly_single_heatmap_visible = true;
      }
      if (res.default_bin_size === 'cellbin' && res.is_cell_bin_heatmap && json.layers[i].type !== 'image') {
        // 默认cellbin时则展示cellbin heatmap
        json.layers[i].visible = json.layers[i].genename === 'cellbin_heatmap'
      }
    }
    let no_image = false;
    if (!hasImage && layer.length >= 1) {
      // 没有ssDNA或dapi图像时，默认打开第一组image的第一个图像
      json.layers[0].visible = true;
      no_image = true;
    }
    if (!hasImage && !allToolsButton.genelistFile && !no_image) {
      json.layers[0].visible = true;
    }
  }
  const heatmapName = json.layers.filter((item: any) => item.type !== 'image' && item.visible);
  allToolsButton.genelistFile = heatmapName ? heatmapName[0]?.genefile : 'null';
  allToolsButton.genelistName = heatmapName ? heatmapName[0]?.name : 'null';
  allToolsButton.genelistSn = heatmapName ? heatmapName[0]?.processid : 'null';
  allToolsButton.defaultGefFile = allToolsButton.genelistFile;
  if (document.getElementsByClassName('gene-view')) {
    (document.getElementsByClassName('gene-view')[0].getElementsByClassName('selecte-text')[0] as HTMLElement).innerHTML = (allToolsButton.genelistName as string)?.toLocaleLowerCase().includes('metabon') ? i18n.metabon : (allToolsButton.genelistName as string)?.toLocaleLowerCase().includes('protein') ? i18n.protein : i18n.gene;
  }
  if (!initShowLegend) {
    json.imageTools.showLegend = false;
    viewer.showLegend.value = false;
  }
  viewer.binSize.value = _param.binSize ? _param.binSize : res.default_bin_size;
  allToolsButton.genelistName = _param.layername ? _param.layername : allToolsButton.genelistName;
  if (source === 'demo' && demo_res && demo_res.data) {
    const data = JSON.parse(demo_res.data);
    viewer.allDataSource = data.allDataSource;
    viewer.bin200_self_newlabel_color = data.bin200_self_newlabel_color;
    viewer.bin200_self_newlabel = data.self_newlabel_color;
    viewer.self_newlabel_color = data.self_newlabel_color;
    viewer.self_newlabel = data.self_newlabel;
    viewer.saveSelectedKeyVal = data.saveSelectedKeyVal;
    viewer.isLegendOfImage.value = data.isLegendOfImage;
    viewer.isBinsizeAuto.value = data.isBinsizeAuto;
    viewer.showSingleGene.value = undefined;
    viewer.proteinMergeColor.value = data.proteinMergeColor;
    allToolsButton.genelistFile = data.genelistFile;
    viewer.color_scale = data.color_scale;
    // demo示例的缩放比例做下特殊处理，先存起来再恢复
    viewer.initCrossSectionScaleForDemoData = data.crossSectionScale;
    delete data.crossSectionScale;
    viewer.state.restoreState(data);
    viewer.jsonCloneObj = [];
    viewer.genePanelState.reset();
    isSelectView(true, 'layer-view');
  } else {
    viewer.state.restoreState(json);
  }
  viewer.resetoreJson(JSON.parse(JSON.stringify(viewer.state.toJSON())));
  if (allToolsButton.isTransform) allToolsButton.isTransform = await getRegisterLayer() ?? false;        //  判定是否可以配准
  if (!allToolsButton.isTransform && document.getElementsByClassName('neuroglancer-icon-transform')[0]) {
    document.getElementsByClassName('neuroglancer-icon-transform')[0].classList.add('disabled')
    viewer.allToolsControlDisable.push('neuroglancer-icon-transform', document.getElementsByClassName('neuroglancer-icon-transform')[0])
  }
  get_umap_binsize();
  setTimeout(() => {
    if (Object.keys(viewer.tissueGefOutlineGeoData).length > 0) {
      viewer.tissueGefOutline = new tissueGefOutline(viewer, viewer.navigationState);
      viewer.tissueGefOutline.setGeoData(viewer.tissueGefOutlineGeoData[allToolsButton.genelistFile]);
    }
  }, 2000);
  if (_param.flag === 'v2') await getWorkflowParam();
}

interface ViewStateJSON {
  [key: string]: any
}
interface urlOption {
  sn: string,
  source: string,
  IF_color?: string,
  name?: string,
  num_channels?: number;
  gray_level_elbow?: number,
  max_gray_level?: number,
  file?: string
}
export interface getNgLayersParam {
  layerTree: Array<any>,
  binsize: string,
  json: ViewStateJSON,
  options: urlOption,
  layerState: any,
  isChangeBinsize: boolean
}
export function handleLayerJson(info: SingleLayerInfo, binsize: string = 'cellbin', json: ViewStateJSON, options: urlOption, layerState = {}, parentName?: string | undefined, isChangeBin: boolean = false) {
  const { sn, name } = options;
  let gef_file = parentName?.includes('protein') || name?.includes('protein') ? protein_gef : transcript_gef;
  if (info.file?.includes('.gef') && allToolsButton.genelistFile === info.file && !info['has_data'][binsize]) {
    new message(i18n.noMatchBinszie, 'error');
    viewer.binSize.value = layerState['binsize'];
    binsize = layerState['binsize'];
    allToolsButton.is_match_binsize = false;
  }
  if (_param.visible && ((_param.visible === info.file) || (_param.layername === info.name) || (_param.visible === info['has_data'][binsize]?._file)) && !info['has_data']?.[binsize]?.[binsize]) {
    new message(i18n.noMatchBinszie, 'error');
    viewer.binSize.value = layerState['binsize'];
    binsize = layerState['binsize'];
    console.log(viewer.binSize.value)
    allToolsButton.is_match_binsize = false;
  }
  // const hasData_binsize = binsize === 'cellbin' ? info['has_data'][binsize] || info['has_data']['default'] : info['has_data'][binsize];
  const hasData_binsize = info['has_data'][binsize]
  if (!info.type && hasData_binsize) {
    const newLayerInfo = Object.assign({
      type: info['has_data'][binsize].type,
      suffix: info['has_data'][binsize].suffix,
    }, info)
    handleLayerJson(newLayerInfo, binsize, json, options, layerState, parentName)
    return
  }
  const hasData = info['has_data'][binsize] || false;
  const h5ad_file = info['has_data'][binsize]?._file;
  const cellbin_file = binsize === 'cellbin' ? info['has_data'][binsize]?._file : '';
  if (h5ad_file?.includes('.h5ad')) {
    is_h5ad_file = true;
    if (!viewer.h5adfile_cellbinfile[h5ad_file]) viewer.h5adfile_cellbinfile[h5ad_file] = '';
    getH5adStatictis(h5ad_file);
  }
  switch (info.type) {
    case 'image':
    case 'rawimage':
      // 判定是否为IF蛋白图层
      const _parentNameArr = ['dapi', 'image_layer', 'ssdna', 'image'];
      const _infoNametest = /ssdna|dapi|image/;
      const is_DAPI_ssDNA = (parentName && _parentNameArr.includes(parentName?.toLocaleLowerCase())) && (_infoNametest.test(info.name.toLocaleLowerCase()) && !info.name.toLocaleLowerCase().includes('mask'));
      if (is_DAPI_ssDNA) hasImage = true;
      const shader: image_layer_info_interface = getNewRpiImageType(options)
      if (!info.name.includes('-')) info.name = `${parentName}-${info.name}`;
      if (!isChangeBin) {
        let shaderControls = {};
        if (info.max_gray_level) {
          shaderControls['normalized'] = {
            "range": [0, info.max_gray_level],
          }
        }
        json.layers.push({
          "type": 'image',
          "source": `precomputed://${local_host_SN_flag}/${info.type}/${info.suffix.replace('rawimage/', '')}`,
          "tab": "rendering",
          "blend": "additive",
          "shader": shader.image_shader,
          // "shader": "#uicontrol invlerp normalized\n#uicontrol float brightness slider(min=-1, max=1)\n#uicontrol float contrast slider(min=-3, max=100, step=0.01, default=1.0)\nvoid main() {\n emitRGB(color*(normalized()+brightness)*exp(contrast));\n#uicontrol vec3 color color(default=\"#" + options.IF_color + "\")\n}",
          "shaderControls": shaderControls,
          "genetype": "proteinimage_image",
          "hasData": shader.num_channels as number > 1 ? true : typeof (hasData) === 'boolean' ? hasData : hasData[binsize],
          "visible": is_DAPI_ssDNA ? is_DAPI_ssDNA : layerState[info.name] !== undefined ? !!layerState[info.name] : false,
          "genecolor": shader.genecolor,
          "genename": info.name,
          "name": info.name,
          "opacity": 1.0
        })
        if (json.layers.length > 0 && json.layers[0].visible) initShowLegend = true
      }
      break
    case 'annotation':
    case 'default':
      // genetabletype = 'annotation';
      // json.geneTableUrl = `${local_host_SN_flag}/annotation`;
      gef_file = gef_file ? gef_file : allToolsButton.defaultGefFile;
      if (info.name.indexOf('cluster') > -1 || info.name.indexOf('UMAP') > -1) {
        if (hasData[binsize]) h5ad_file_binsize = binsize;
        h5ad_file_binsize = binsize;
        const umap_shader = info.name.indexOf('UMAP') > -1 ? `
        #uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
        void main() { 
          discardByFloatProperty(float(prop_c()));
          prop_category_x();
          prop_category_y();            
         setColor(vec3(1.0,1.0,0.0));
         //  setColor(colormapDefaultNonlinear(prop_color(),vec3[9](vec3 (0.05,0.20,0.51),vec3 (0.00,0.37,0.64),vec3 (0.04,0.53,0.73),vec3 (0.00,0.76,0.60),vec3 (0.95,0.83,0.22),vec3 (0.96,0.69,0.20),vec3 (0.95,0.56,0.22),vec3 (0.91,0.38,0.14),vec3 (0.85,0.12,0.12))));
          setPointMarkerFactor(1.0);
          setPointMarkerOpacityFactor(oFactor - 0.01);
        }` : `#uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
        void main() { 
          discardByFloatProperty(float(prop_c()));
         setColor(vec3(1.0,1.0,0.0));
         //  setColor(colormapDefaultNonlinear(prop_color(),vec3[9](vec3 (0.05,0.20,0.51),vec3 (0.00,0.37,0.64),vec3 (0.04,0.53,0.73),vec3 (0.00,0.76,0.60),vec3 (0.95,0.83,0.22),vec3 (0.96,0.69,0.20),vec3 (0.95,0.56,0.22),vec3 (0.91,0.38,0.14),vec3 (0.85,0.12,0.12))));
          setPointMarkerFactor(1.0);
          setPointMarkerOpacityFactor(oFactor - 0.01);
        }`;
        let cluster_v = layerState[info.name] !== undefined ? !!layerState[info.name] : false
        if (_param.visible) {
          cluster_v = _param.layername === info.name;
        }
        if (h5ad_file && h5ad_file.includes('cellbin.gef')) {
          if (!viewer?.self_newlabel_color[h5ad_file]) viewer.self_newlabel_color[h5ad_file] = {};
          if (!viewer?.self_newlabel[h5ad_file]) viewer.self_newlabel[h5ad_file] = {};
        } else if (h5ad_file && !viewer?.bin200_self_newlabel_color[h5ad_file]) {
          if (!viewer?.bin200_self_newlabel_color[h5ad_file]) {
            viewer.bin200_self_newlabel_color[h5ad_file] = {};
            viewer.bin200_self_newlabel_color[h5ad_file][binsize] = {};
          }
          if (!viewer?.bin200_self_newlabel[h5ad_file]) {
            viewer.bin200_self_newlabel[h5ad_file] = {};
            viewer.bin200_self_newlabel[h5ad_file][binsize] = {};
          }
        }
        json.layers.push({
          // binsize聚类
          "type": info.type,
          "source": [
            info.name.includes('UMAP') ? `precomputed://${local_host_SN_flag}/annotation/umap/${gef_file}/${h5ad_file}/${info.suffix.replace('umap/', '')}` : `precomputed://${local_host_SN_flag}/annotation/${info.suffix}/${h5ad_file}`,
            `precomputed://${local_host_SN_flag}/annotation/h5ad/label/${h5ad_file}`,
          ],
          "tab": "rendering",
          "name": info.name,
          "visible": cluster_v, // 暂时屏蔽
          "hasData": typeof (hasData) === 'boolean' ? hasData : hasData[binsize],
          // "visible": layerState[info.name] !== undefined ? !!layerState[info.name] : false,
          // "hasData": false,
          "cellbindnb": "cellbindnb",
          "genetype": 'segmentation',
          "shader": umap_shader,
          'segmentColors': viewer?.binSize.value === 'cellbin' ? viewer?.self_newlabel_color?.[h5ad_file] ?? {} : viewer?.bin200_self_newlabel_color?.[h5ad_file]?.[binsize] ?? {},
          genename: info.file ?? info.name,
          genefile: h5ad_file,
          menupath: info?.menu_path,
        });
      } else {
        // heatmap
        if (json.imageTools) json.imageTools.showColorbar = typeof (hasData) === 'boolean' ? hasData : hasData[binsize];
        const heatmapShader = `#uicontrol float sizeFactor slider(min=0.0, max=1.0, default=1.0, step=0.01); 
        #uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
        #uicontrol invlerp midFilter(property="midCount", clamp=false)
        void main() {
          float filterValue = midFilter();
          if(filterValue < 0.0 || filterValue > 1.0){
            discard;
          }else {
            setColor(vec3(1.0, 0.0, 0.0));
            prop_midCount();
            setPointMarkerFactor(sizeFactor);
            setPointMarkerOpacityFactor(oFactor - 0.01);
          }
        }`;
        is_label_gef = is_label_gef ? is_label_gef : info.file?.includes('label') ?? false;
        let heatmap_v = layerState[info.name] !== undefined ? !!layerState[info.name] : (typeof (hasData) === 'boolean' ? hasData : hasData[binsize])
        if (_param.visible) {
          heatmap_v = (_param.visible === info.file) || (_param.layername === info.name)
        }
        json.layers.push({
          "type": info.type,
          "source": `precomputed://${local_host_SN_flag}/annotation/${info.suffix}`,
          "tab": "rendering",
          "name": info.name,
          "visible": heatmap_v,
          "hasData": typeof (hasData) === 'boolean' ? hasData : hasData[binsize],
          "cellbindnb": "cellbindnb",
          "genetype": 'annotationdnb',
          "genecolor": viewer?.allDataSource?.colorscale.genecolor ? viewer?.allDataSource?.colorscale.genecolor : "Default",
          crossSectionAnnotationSpacing: 3,
          "heatmap": info.name.indexOf('heatmap') > -1 ? true : false,
          shader: heatmapShader,
          genename: info.file ?? info.name,
          genefile: info.file ?? info.name,
          genesn: info.sample_id,
          processid: info.process_id,
          taskid: info?.task_id,
          menupath: info?.menu_path,
          filename: info?.file_name
        });
        if (heatmap_v) allToolsButton.genelistFile = info.file
        statisticsFile.push(info.file as string)
      }
      break

    case 'border_annotation':
      let cell_border_v = layerState[info.name] !== undefined ? !!layerState[info.name] : (typeof (hasData) === 'boolean' ? hasData : hasData[binsize]);
      if (_param.layername) cell_border_v = (_param.visible === info.file) || (_param.layername === info.name);
      const genefile = info.has_data[binsize]._file ?? info.name;
      if (genefile && genefile.includes('cellbin.gef')) {
        if (!viewer?.self_newlabel_color[genefile]) viewer.self_newlabel_color[genefile] = {};
        if (!viewer?.self_newlabel[genefile]) viewer.self_newlabel[genefile] = {};
      } else {
        if (!viewer?.bin200_self_newlabel_color[genefile]) viewer.bin200_self_newlabel_color[genefile] = {};
        if (!viewer?.bin200_self_newlabel[genefile]) viewer.bin200_self_newlabel[genefile] = {};
      }
      console.log(genefile, viewer.self_newlabel)
      json.layers.push({
        "type": 'annotation',
        "source": [
          `precomputed://${local_host_SN_flag}/annotation/cellbin/${cellbin_file}`,
          `precomputed://${local_host_SN_flag}/segmentation/cellbin/${cellbin_file}/labels`,
        ],
        "tab": "rendering",
        "name": info.name,
        "visible": cell_border_v,
        "hasData": typeof (hasData) === 'boolean' ? hasData : hasData[binsize],
        "cellbindnb": "cellbindnb",
        "genetype": 'segmentation',
        "shader": `
          #uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
          void main() {
            getCellArea(prop_area());
            setCellIdVarying(prop_id());
            prop_clusterID();
            prop_cellTypeID();
            setColor(vec3(1.0,1.0,1.0));
          }`,
        "shaderControls": {
          "oFactor": 1.0
        },
        'segmentColors': viewer?.binSize.value === 'cellbin' ? viewer?.self_newlabel_color[genefile] : viewer?.bin200_self_newlabel_color[genefile][binsize],
        'genesn': info.has_data[binsize]._sample_id,
        processid: info.has_data[binsize]._process_id,
        taskid: info.has_data[binsize]?._task_id,
        menupath: info.has_data['cellbin']?._menu_path,
        genename: "cellbin_heatmap",
        filename: info.has_data[binsize]?._file_name,
        genefile: genefile
      });
      break
    case 'cellbinHeatMap':
      const visi = typeof (hasData) === 'boolean' ? hasData : hasData[binsize];
      json.layers.push({
        "type": 'annotation',
        "source": [
          `precomputed://${local_host_SN_flag}/annotation/cellbin/${cellbin_file}`
        ],
        "tab": "rendering",
        "name": info.name,
        "hasData": visi,
        "cellbindnb": "cellbindnb",
        "genecolor": viewer?.allDataSource?.colorscale.genecolor ? viewer?.allDataSource?.colorscale.genecolor : "Default",
        "genetype": "cellbin",
        "heatmap": true,
        "shader": `
          #uicontrol float oFactor slider(min=0.0, max=1.0, default=1.0, step=0.01);
          void main() {
            prop_expCount();
            getCellArea(prop_area());
            setCellIdVarying(prop_id());
            setClusterIDVarying(prop_clusterID());
          }`,
        genename: "cellbin_heatmap",
        genefile: info.has_data[binsize]._file ?? info.name,
        visible: (_param.layername === info.name),
        genesn: info.has_data[binsize]._sample_id ?? info.name,
        processid: info.has_data[binsize]?._process_id,
        taskid: info.has_data[binsize]?._task_id,
        menupath: info.has_data[binsize]?._menu_path,
        filename: info.has_data[binsize]?._file_name
      });
      if (allToolsButton.genelistName === info.name) allToolsButton.genelistFile = info.has_data[binsize]._file;
      break
    case 'segmentation':
      // segmentation cellbin
      if (json.imageTools) json.imageTools.showLegend = typeof (hasData) === 'boolean' ? hasData : hasData[binsize]
      let cell_v = layerState[info.name] !== undefined ? !!layerState[info.name] : (typeof (hasData) === 'boolean' ? hasData : hasData[binsize]);
      if (_param.visible) cell_v = (_param.visible === info.file) || (_param.layername === info.name);
      const _genefile = info.has_data[binsize]._file ?? info.name;
      if (viewer?.binSize.value === 'cellbin') {
        if (!viewer?.self_newlabel_color[_genefile]) viewer.self_newlabel_color[_genefile] = {};
        if (!viewer?.self_newlabel[_genefile]) viewer.self_newlabel[_genefile] = {};
      } else {
        if (!viewer?.bin200_self_newlabel_color[_genefile]) viewer.bin200_self_newlabel_color[_genefile] = {};
        if (!viewer?.bin200_self_newlabel[_genefile]) viewer.bin200_self_newlabel[_genefile] = {};
      }
      json.layers.push({
        "type": info.type,
        "source":
          [`precomputed://${local_host_SN_flag}/${info.type}/${info.suffix}/${cellbin_file}`,
          `precomputed://${local_host_SN_flag}/${info.type}/${info.suffix}/${cellbin_file}/labels`,
          ],
        "tab": "rendering",
        "name": info.name,
        "hasData": typeof (hasData) === 'boolean' ? hasData : hasData[binsize],
        "visible": cell_v,
        "selectedAlpha": 1,
        "genetype": info.type,
        "genename": sn + '_cellbin',
        genefile: _genefile,
        'segmentColors': viewer?.binSize.value === 'cellbin' ? viewer?.self_newlabel_color[_genefile] : viewer?.bin200_self_newlabel_color[_genefile][binsize],
        genesn: info.has_data[binsize]._sample_id,
        processid: info.has_data[binsize]._process_id,
        menupath: info.has_data[binsize]._menu_path,
        taskid: info.has_data[binsize]?._task_id,
        filename: info.has_data[binsize]?._file_name
      },
        {
          "type": 'annotation',
          "source": `precomputed://${local_host_SN_flag}/annotation/${info.suffix}/${cellbin_file}/bin1`,
          "tab": "rendering",
          "genename": "annotationCellbin",
          "shader": `void main() {
            setColor(defaultColor());
            setPointMarkerFactor(1.5*sqrt(float(prop_area())/3.14159));
            setPointMarkerOpacityFactor(0.);
          }`,
          "name": `hover_annotation`,
          "shaderControls": {
            "oFactor": 0.5
          },
          "hasData": typeof (hasData) === 'boolean' ? hasData : hasData[binsize],
          "visible": cell_v,
          "genetype": info.type,
          genesn: info.has_data[binsize]._sample_id,
          processid: info.has_data[binsize]._process_id,
          menupath: info.has_data[binsize]._menu_path,
          taskid: info.has_data[binsize]?._task_id,
          filename: info.has_data[binsize]?._file_name
        }
      )
      break
    default:
      break
  }
}
/**
 * layerTree: layer面板结构从viewer.layerTree中获取
 * binsize：要展示的bin：viewer.binsize.value
 * json: json为空对象时需自组
 * urlOption: 存储sn，source
 * layerState：未知
 * local_host_SN_flag: 路由
 * allToolsButton: 工具
 * _check_image_Color：颜色数组
 */
export async function getNgLayers(param: getNgLayersParam, is_gene_or_protein?: any, local_host_SN_flag: string = '', allToolsButton: any = {}, _check_image_Color: string[] = []) {
  // layerTree: Array<any>, binsize: string = 'bin100', json: ViewStateJSON, options: urlOption, layerState = {}) {
  switch (is_gene_or_protein?.str) {
    case 'mutil':
      param.json.layers.forEach((item: any) => {
        if (item.genetype === 'proteinimage') {
          item.source = `precomputed://${local_host_SN_flag}/image/gene/${item.genename}/${allToolsButton.genelistFile}/${param.binsize}`;
          item['hasData'] = true;
        }
        if (item.genename === 'gene_heatmap_layer' && item.source.includes('many_gene')) {
          const _bin = item.source.split('/')
          item.source = item.source.replace(_bin[_bin.length - 1], param.binsize);
        }
      });
      param.json.imageTools.showLegend = true;
      break;
    case 'heatmap':
      param.json.layers.forEach((item: any) => {
        if (item.heatmap && item.genename === allToolsButton.genelistFile) {
          item.source = item.source.replace(/bin.*/, param.binsize)
        }
      });
      param.json.imageTools.showLegend = true;
      break;
    default:
      if (param.isChangeBinsize) {
        const _layers = param.binsize === 'cellbin' ? param.json.layers.filter((item: any) => item?.type === 'image' && item.genetype !== 'proteinimage') : param.json.layers.filter((item: any) => item?.type === 'image');  // 保留image图像的变化
        param.json.layers = _layers;
      }
      if (_check_image_Color.length < 1) _check_image_Color = ['0000ff', '00ffff', 'ff00ff', '00ff00', 'ff0000', 'ff9600'];
      for (let i = 0; i < param.layerTree.length; i++) {
        const layerItem = param.layerTree[i]
        let layerLevel = layerItem.depth || 2
        if (_param.visible && layerItem.name.includes('image')) continue;
        // 兼容新rpi数据结构，新rpi的层级为3，旧格式为2
        if (layerLevel > 2) {
          for (let j = 0; j < layerItem.children.length; j++) {
            const childLayerItem = layerItem.children[j]
            param.options.IF_color = 'ffffff';
            if (childLayerItem.children && childLayerItem.children.length > 0) {
              childLayerItem.children.forEach((sunItem: SingleLayerInfo) => {
                param.options.gray_level_elbow = sunItem?.gray_level_elbow;
                param.options.max_gray_level = sunItem?.max_gray_level;
                param.options.IF_color = sunItem.name.toLocaleLowerCase()?.includes('label') ? sunItem?.color : 'ffffff';
                if (childLayerItem.name.indexOf('IF') > -1) {
                  if (sunItem.name.includes('Image')) {
                    param.options.IF_color = sunItem?.color ?? _check_image_Color.length > 0 ? _check_image_Color[0] : 'ffffff';
                    _check_image_Color.shift();
                  } else {
                    param.options.IF_color = 'ffffff';
                  }
                } else if (childLayerItem.name.indexOf('DAPI') > -1) {
                  // DAPI 单独颜色
                  if (sunItem.name.includes('Image')) {
                    param.options.IF_color = sunItem?.color ?? _check_image_Color[0];
                    _check_image_Color.shift();
                  } else {
                    param.options.IF_color = sunItem?.color ?? 'ffffff'
                  }
                }
                if (sunItem.name.indexOf('UMAP') > -1) {
                  is_h5ad_file = is_h5ad_file ? is_h5ad_file : sunItem.has_data?.bin200?.bin200;
                }
                if (sunItem.name.includes('cluster')) {
                  if (sunItem.has_data.cellbin?._file) {
                    statisticsFile.push(sunItem.has_data.cellbin._file);
                    viewer.h5adfile_cellbinfile[sunItem.has_data.cellbin._file] = '';

                  }
                }
                if (sunItem.name.includes('heatmap')) {
                  if (childLayerItem.name.includes('transcript')) {
                    transcript_gef = sunItem.file as string
                  } else {
                    protein_gef = sunItem.file as string
                  }
                  statisticsFile.push(sunItem.file as string);
                  if (sunItem.has_data?.cellbin) statisticsFile.push(sunItem.has_data.cellbin._file as string);
                }
                param.options.name = sunItem.name;
                param.options.num_channels = sunItem.num_channels;
                param.options.IF_color = sunItem.name.toLocaleLowerCase()?.includes('label') ? sunItem?.color ?? 'ffffff' : param.options.IF_color;
                param.options.file = sunItem.file;
                handleLayerJson(sunItem, param.binsize, param.json, param.options, param.layerState, childLayerItem.name, param.isChangeBinsize)
              })
            } else {
              param.options.name = childLayerItem.name;
              param.options.num_channels = childLayerItem.num_channels;
              param.options.gray_level_elbow = childLayerItem?.gray_level_elbow;
              param.options.max_gray_level = childLayerItem?.max_gray_level;
              param.options.file = childLayerItem.file;
              handleLayerJson(childLayerItem, param.binsize, param.json, param.options, param.layerState, undefined, param.isChangeBinsize)
            }
          }
        } else {
          param.options.IF_color = 'ffffff';
          if (layerItem.children && layerItem.children.length > 0) {
            layerItem.children.forEach((sunItem: SingleLayerInfo) => {
              param.options.gray_level_elbow = sunItem?.gray_level_elbow;
              param.options.max_gray_level = sunItem?.max_gray_level;
              param.options.IF_color = sunItem.name.toLocaleLowerCase()?.includes('label') ? sunItem?.color : 'ffffff';
              if (sunItem.name.indexOf('IF') > -1) {
                const color = _check_image_Color.length > 0 ? _check_image_Color[0] : 'ffffff';
                param.options.IF_color = sunItem?.color ?? color;
                _check_image_Color.shift();
              } else if (sunItem.name.indexOf('DAPI') > -1) {
                param.options.IF_color = sunItem?.color ?? _check_image_Color[0];
                _check_image_Color.shift();
              } else {
                param.options.IF_color = sunItem?.color ?? 'ffffff'
              }
              if (sunItem.name.indexOf('UMAP') > -1) {
                is_h5ad_file = is_h5ad_file ? is_h5ad_file : sunItem.has_data?.bin200?.bin200
              }
              if (sunItem.name.includes('cluster')) {
                if (sunItem.has_data.cellbin?._file) {
                  statisticsFile.push(sunItem.has_data.cellbin._file);
                  viewer.h5adfile_cellbinfile[sunItem.has_data.cellbin._file] = '';
                }
              }
              if (sunItem.name.includes('heatmap')) {
                if (sunItem.name.includes('transcript')) {
                  transcript_gef = sunItem.file as string
                } else {

                  protein_gef = sunItem.file as string
                }
                statisticsFile.push(sunItem.file as string);
                if (sunItem.has_data?.cellbin) statisticsFile.push(sunItem.has_data.cellbin._file as string);
              }
              param.options.name = sunItem.name;
              param.options.num_channels = sunItem.num_channels;
              param.options.IF_color = sunItem.name.toLocaleLowerCase()?.includes('label') ? sunItem?.color ?? 'ffffff' : param.options.IF_color;
              param.options.file = sunItem.file;
              handleLayerJson(sunItem, param.binsize, param.json, param.options, param.layerState, layerItem.name, param.isChangeBinsize)
            })
          } else {
            param.options.gray_level_elbow = layerItem?.gray_level_elbow;
            param.options.max_gray_level = layerItem?.max_gray_level;
            param.options.name = layerItem.name;
            param.options.num_channels = layerItem.num_channels;
            param.options.file = layerItem.file;
            handleLayerJson(layerItem, param.binsize, param.json, param.options, param.layerState, undefined, param.isChangeBinsize)
          }
        }
      }
      console.log(param.json, param.layerTree, gef_binlist);
      break;
  }
}
// async function getPermission(){
//   let req = async (): Promise<any> => {
//     return await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/get_permission?module=stereoMap`, {}, responseJson);
//   };
//   return await req();
// }
async function getLayers() {
  let req = async (): Promise<any> => {
    return await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/layerInfo`, {}, responseJson);
  };
  layerinfo_permission.layerinfo = await req();
}
async function getDemoJson(user_operate_id: string) {
  let req = async (): Promise<any> => {
    return await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/demo_default_json/${user_operate_id}`, {}, responseJson);
  };
  return await req();
}

async function getFilePathPermission() {
  let response;
  await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/data_path`, {}, responseJson).then((res) => {
    response = res;
    if (res.code === 0) {
      const responseData = res.data;
      for (let i in responseData) {
        if (i === 'permissions') {
          layerinfo_permission.permission = responseData[i];
          break;
        }
      }
      interceptorHeaderInfo.value = Object.assign(interceptorHeaderInfo.value, responseData);
      delete interceptorHeaderInfo.value['permissions'];
      delete interceptorHeaderInfo.value['task_info'];
    } else if (res.msg.includes('expired')) {
      displayLoadingNone();
      noPermision();
    } else {
      new message(res.msg, 'info', true);
    }
  });
  return response;
}
let getRegisterLayer_taskid = '';
let getRegisterLayer_processid = '';
let getRegisterLayer_sampleid = '';
let getRegisterLayer_layer: any = false;
export async function getRegisterLayer(flag?: string) {
  if (flag === 'taskid') {
    return getRegisterLayer_taskid
  } else if (flag === 'processid') {
    return getRegisterLayer_processid
  } else if (flag === 'sampleid') {
    return getRegisterLayer_sampleid
  } else if (flag === 'layer') {
    return getRegisterLayer_layer
  } else {
    let req = async (): Promise<any> => {
      return await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/get_register_layer`, {}, responseJson);
    };
    let res = await req();
    getRegisterLayer_taskid = res.data.task_id;
    getRegisterLayer_processid = res.data.process_id;
    getRegisterLayer_sampleid = res.data.sample_id;
    getRegisterLayer_layer = res.data.layers;
    return res.data.layers
  }
}
let workflow_param: { [K: string]: string | boolean | any } = {}
export async function getWorkflowParam(flag?: string | undefined, file_name?: string, taskid?: string, layer_name?: string) {
  if (flag) {
    // 先置空
    workflow_param['cgef'] = "";
    workflow_param['cellbinH5AD'] = "";
    // 判断是转录还是蛋白，为下面获取cgef和cellbinH5AD参数作判断
    let isProtein = layer_name && layer_name.includes("protein");
    console.log(isProtein)
    workflow_param.resultFiles?.forEach((item: any) => {
      if (item.path.includes('cellbin.gef') && item.subnet === 'hpc' && (isProtein ? item.name.includes('protein') : !item.name.includes('protein'))) {
        // 优先填充adjusted文件
        if (!workflow_param['cgef'].includes('adjusted')) {
          workflow_param['cgef'] = item.path;
        }
      }
      if (item.name === file_name && item.subnet === 'hpc') workflow_param['bgef'] = item.path;
      if (item.name === file_name) console.log(item);
      if (item.path.includes('cell.cluster.h5ad') && item.subnet === 'hpc' && (isProtein ? item.name.includes('protein') : !item.name.includes('protein'))) {
        // 优先填充adjusted文件
        if (!workflow_param['cellbinH5AD'].includes('adjusted')) {
          workflow_param['cellbinH5AD'] = item.path;
        }
      }
    })
    return workflow_param
  } else {
    const task_id = _param.flag === 'tool' ? taskid || await getRegisterLayer('taskid') : _param.DataID;
    if (task_id) {
      let req = async (): Promise<any> => {
        return await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/get_register_param/${task_id}`, {}, responseJson);
      };
      let res = await req();
      if (res.data?.taskDetail?.req) {
        let analyseInputs = res.data?.taskDetail?.req.analyseParam.inputs
        if (analyseInputs) {
          // 遍历对象的键，通过后缀匹配来获取对应的值
          for (let key in analyseInputs) {
            if (key.endsWith('.Cellbin')) {
              workflow_param['Cellbin'] = analyseInputs[key];
            }
            if (key.endsWith('.ImageTar')) {
              workflow_param['ImageTar'] = analyseInputs[key];
            }
            if (key.endsWith('.ImagePreDir') || key.toLowerCase().endsWith('.imagepredir')) {
              workflow_param['ImagePreDir'] = analyseInputs[key];
            }
            if (key.endsWith('.Reference')) {
              workflow_param['Reference'] = analyseInputs[key];
            }
            if (key.endsWith('.Tissue')) {
              workflow_param['Tissue'] = analyseInputs[key];
            }
            if (key.endsWith('.MASK')) {
              workflow_param['MASK'] = analyseInputs[key];
            }
          }
        }
        // workflow_param = JSON.parse(res.data?.taskDetail?.reqJson);
        // workflow_param['resultFiles'] = res.data.resultFiles;
        // let rawcontents = res.data.rawContent[0];
        // for (let i in rawcontents) {
        //   if (i.includes('Cellbin')) {
        //     workflow_param['Cellbin'] = rawcontents[i];
        //   }
        //   if (i.toLocaleLowerCase().includes('imagetar')) {
        //     workflow_param['ImageTar'] = rawcontents[i];
        //   }
        //   if (i.toLocaleLowerCase().includes('imagepredir')) {
        //     workflow_param['ImagePreDir'] = rawcontents[i];
        //   }
        //   if (i.toLocaleLowerCase().includes('reference')) {
        //     workflow_param['Reference'] = rawcontents[i];
        //   }
        //   if (i.toLocaleLowerCase().includes('tissue')) {
        //     workflow_param['Tissue'] = rawcontents[i];
        //   }
        // }
        // 处理 DataDir 的两种规则
        const outputDir = res.data?.taskDetail?.req.analyseParam.outputDir;
        const taskId = res.data?.taskId;

        if (outputDir === '/Files/ResultData/Workflow' || outputDir === '/Files/ResultData/Workflow/[taskId]') {
          // 规则1: outputdir值的/Files/ResultData/Workflow或/Files/ResultData/Workflow/[taskId]格式的，实际输出目录/Files/ResultData/Workflow/任务编号
          workflow_param['DataDir'] = `/Files/ResultData/Workflow/${taskId}/`;
        } else if (outputDir.includes('[taskId]')) {
          // 规则2: 如果有[taskId]都要替换成实际的任务编号
          workflow_param['DataDir'] = outputDir.replace(/\[taskId\]/g, taskId) + (outputDir.endsWith('/') ? '' : '/');
        } else {
          // 其他情况，输出格式就是outputdir值本身
          workflow_param['DataDir'] = outputDir + (outputDir.endsWith('/') ? '' : '/');
        }

        workflow_param['Cellbin'] = workflow_param['Cellbin'] ?? false;
        workflow_param['backupPath'] = res.data?.taskDetail.backupPath;
        workflow_param['taskId'] = res.data.taskId;
        workflow_param['wfTag'] = res.data.wfTag;
        workflow_param['sampleId'] = res.data.sampleId;
        return workflow_param
      } else {
        return {};
      }
    } else {
      return {}
    }
  }
}

// 防止eval ts编译中报错
function evil(fn: any) {
  var Fn = Function;  //一个变量指向Function，防止有些前端编译工具报错
  return new Fn('return ' + fn)();
}

export function _404Found() {
  (document.getElementById('neuroglancer-container') as HTMLElement).innerHTML = '';
  document.getElementById('neuroglancer-container')?.appendChild(
    makeElementUtil('div', ['noPermision'], { 'style': 'text-align: center; position: relative; top: 50%; transform: translateY(-50%)' },
      `<img src="static/404.png" /><p style="font-size: 1.5em;font-family: MicrosoftYaHei; color: #999999;">404, ERROR</p>`)
  );
  (document.getElementById('neuroglancer-container') as HTMLElement).style.background = '#1F2132';
  console.log(document.getElementById('neuroglancer-container'), 'nopermision,404')
  new message(i18n.contactAdmin, 'error');
}

async function getAllStatictis() {
  let p: any = [];
  if (statisticsFile.length > 0) {
    statisticsFile = comm_arr_filter(statisticsFile);
    for (let i = 0; i < (statisticsFile as string[]).length; i++) {
      if (statisticsFile[i]) {
        //  const gef_file = statisticsFile[i].split('_*_')
        //  const is_cellbin_key = gef_file[gef_file.length - 1].includes('cellbin') ? 'cell_bin_info_statistics' : 'gef_info_statistics';
        let req = new Promise(async (resolve, reject) => {
          const response = await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/annotation/gef_info_statistics/${statisticsFile[i]}`, {}, responseJson);
          try {
            resolve(response);
          } catch (error) {
            reject(error);
          }
        }).then((resq: any) => {
          viewer.statisticsInfo[statisticsFile[i]] = resq?.data;
        })
        p.push(await req);
        // 普通bin
      }
    }
    Promise.all(p).then(() => {
    })
  }
}
export async function getH5adStatictis(file: string) {
  if (!viewer.statisticsInfo[file]) {
    const response = await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/annotation/h5ad_statistics/${file}`, {}, responseJson);
    if (response.code === 0) {
      viewer.statisticsInfo[file] = response.data;
    }
  }
}

export function setvisibleLayer() {
  if (_param.visible && _param.layername.includes('UMAP')) {
    const umap = viewer.layerManager.getLayerByName(_param.layername, 'name');
    if (_param.visible.includes('heatmap')) allToolsButton.genelistFile = _param.visible
    setTimeout(() => {
      umap?.setVisible(true);
    }, 1500);
  }
}
function get_umap_binsize() {
  let arr: any = [];
  viewer.layerTree.forEach((item: any) => {
    if (item.name === "main_analysis_layer") {
      arr = item.children.filter((subItme: any) => subItme.name.includes('UMAP'))
    }
  });
  if (arr?.length > 0) {
    for (let m = 0; m < arr.length; m++) {
      let a = arr[m].has_data;
      umap_tree_bin_arr = { protein: [], gene: [] };
      for (let i in a) {
        if (a[i]?._file) {
          a[i]._file.includes('protein') ? umap_tree_bin_arr.protein.push(i) : umap_tree_bin_arr.gene.push(i)
        }
      }
    }
    console.log(umap_tree_bin_arr)
  }
}
// export async function getH5adStatictis(file: string){
//   if(!viewer.statisticsInfo[file]){
//     const response =  await cancellableFetchSpecialOk(undefined, `${local_host_SN_flag}/annotation/h5ad_statistics`, {}, responseJson);
//     if(response.code === 0){
//       viewer.statisticsInfo[file] = response.data;
//     }
//   }
// }
